/**
 * A day of heart rate, and the resting figure derived from it (ADR-025).
 *
 * Two different claims live here and are kept apart on purpose. The low,
 * mean and high are what a band measured. The resting figure is ours: many
 * bands write heart rate all day and never write a resting record, so the
 * app works one out rather than leaving the tile empty.
 *
 * Because it is ours, it refuses more readily than it guesses. A wrong
 * resting heart rate is worse than no resting heart rate; it is the kind of
 * number somebody repeats to a doctor.
 *
 * Pure: samples in, numbers out.
 */
/**
 * Outside this and it is not a pulse.
 *
 * A sensor losing skin contact reports zero, and the top of the range is
 * above any human maximum, so anything beyond is the sensor talking about
 * itself rather than about a heart.
 */
const MIN_BPM = 25;
const MAX_BPM = 250;
/** A resting heart rate is a sustained state, not a moment. */
const WINDOW_MS = 30 * 60_000;
/** Below this a window is a handful of readings that happen to be low. */
const MIN_PER_WINDOW = 5;
/** Below this the band was barely worn. */
const MIN_SAMPLES = 30;
/** A day somebody wore it for an hour cannot say what they rest at. */
const MIN_SPAN_MS = 2 * 60 * 60_000;
const plausible = (s) => Number.isFinite(s.bpm) && s.bpm >= MIN_BPM && s.bpm <= MAX_BPM && !Number.isNaN(Date.parse(s.at));
export function dayHeartRate(samples) {
    const usable = samples
        .filter(plausible)
        .map((s) => ({ t: Date.parse(s.at), bpm: s.bpm }))
        .sort((a, b) => a.t - b.t);
    if (usable.length === 0)
        return null;
    const values = usable.map((s) => s.bpm);
    const minBpm = Math.min(...values);
    const maxBpm = Math.max(...values);
    const avgBpm = Math.round(values.reduce((a, b) => a + b, 0) / values.length);
    return { minBpm, avgBpm, maxBpm, restingBpm: resting(usable) };
}
/**
 * The lowest rolling thirty-minute mean of the day.
 *
 * A mean rather than the floor of the window, so one dropped reading inside
 * an otherwise steady half hour moves the answer by a fraction instead of
 * deciding it. Taking the day's single lowest sample would report whatever
 * the sensor caught at its worst moment, and would drift down the longer
 * somebody wore the band.
 */
function resting(sorted) {
    if (sorted.length < MIN_SAMPLES)
        return null;
    if (sorted[sorted.length - 1].t - sorted[0].t < MIN_SPAN_MS)
        return null;
    let best = null;
    // Each sample opens a window; the two pointers only ever move forward.
    let end = 0;
    let sum = 0;
    for (let start = 0; start < sorted.length; start += 1) {
        if (end < start) {
            end = start;
            sum = 0;
        }
        while (end < sorted.length && sorted[end].t - sorted[start].t < WINDOW_MS) {
            sum += sorted[end].bpm;
            end += 1;
        }
        const count = end - start;
        if (count >= MIN_PER_WINDOW) {
            const mean = sum / count;
            if (best === null || mean < best)
                best = mean;
        }
        sum -= sorted[start].bpm;
    }
    return best === null ? null : Math.round(best);
}
//# sourceMappingURL=heart-rate.js.map